import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { usersApi } from '../../../api/users.api';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../components/ui/alert-dialog';

// ============ Freeze User Dialog ============

interface FreezeUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userId: string | null;
    userName: string;
    onSuccess: () => void;
}

export function FreezeUserDialog({ open, onOpenChange, userId, userName, onSuccess }: FreezeUserDialogProps) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [reason, setReason] = useState('');

    const handleFreeze = async () => {
        if (!userId) return;
        if (!reason.trim()) {
            toast({ title: 'Atansyon', description: 'Ou dwe bay yon rezon', type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            await usersApi.freezeAccount(userId, reason);
            toast({ title: 'Siksè', description: 'Kont lan glase', type: 'success' });
            onSuccess();
            onOpenChange(false);
            setReason('');
        } catch (error: any) {
            const msg = error.response?.data?.message || 'Erè';
            toast({ title: 'Erè', description: msg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Glase Kont {userName}?</DialogTitle>
                    <DialogDescription>
                        Itilizatè a pap kapab konekte ankò jiskaske ou deglase kont li.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2">
                    <Label htmlFor="freeze-reason">Rezon (Obligatwa)</Label>
                    <Input
                        id="freeze-reason"
                        value={reason}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                        placeholder="Eg: Aktivite sispèk, demann kliyan..."
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annile</Button>
                    <Button variant="destructive" onClick={handleFreeze} disabled={loading}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Glase Kont
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============ Block Device Dialog ============

interface BlockDeviceDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    deviceId: string | null;
    deviceName: string;
    onSuccess: () => void;
}

export function BlockDeviceDialog({ open, onOpenChange, deviceId, deviceName, onSuccess }: BlockDeviceDialogProps) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [reason, setReason] = useState('');

    const handleBlock = async () => {
        if (!deviceId) return;
        if (!reason.trim()) {
            toast({ title: 'Atansyon', description: 'Ou dwe bay yon rezon', type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            await usersApi.blockDevice(deviceId, reason);
            toast({ title: 'Siksè', description: 'Aparèy la bloke', type: 'success' });
            onSuccess();
            onOpenChange(false);
            setReason('');
        } catch (error: any) {
            const msg = error.response?.data?.message || 'Erè';
            toast({ title: 'Erè', description: msg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Bloke Aparèy: {deviceName}?</DialogTitle>
                    <DialogDescription>
                        Aparèy sa pap kapab itilize pou konekte menm si idantifyan yo bon.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2">
                    <Label htmlFor="block-reason">Rezon (Obligatwa)</Label>
                    <Input
                        id="block-reason"
                        value={reason}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                        placeholder="Eg: Aparèy pèdi, vòlè..."
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annile</Button>
                    <Button variant="destructive" onClick={handleBlock} disabled={loading}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Bloke Aparèy
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============ Generic Confirm Dialog ============

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    onConfirm: () => void;
    loading?: boolean;
}

export function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, loading }: ConfirmDialogProps) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {description}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={loading}>Annile</AlertDialogCancel>
                    <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={loading}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Konfime
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
